/* ============================================================
   DUSK & RELIC — storefront v2 interactions (no deps, namespaced)
   - header: inline predictive search (Section Rendering API →
     sections/dnr-predictive.liquid), mobile drawer, Escape handling
   - bag badge: real cart count via dnr:bag CustomEvent + /cart.js
   - product card: ‹ › arrows toggle macro/worn, dots state
   - tabbed carousel: tab switching + mouse drag-to-scroll with
     click suppression after a drag
   - measures header height → --dnr2-sticky-top for the PDP buy box, and the
     PDP mobile add bar → --dnr2-stickybar-h for the page's bottom padding
   Degrades gracefully: without JS the mega menu still opens on
   hover (pure CSS) and cards swap on hover.
   ============================================================ */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    /* ---------- header: measure height → sticky offset ---------- */
    var header = document.querySelector('[data-dnr2-header]');
    function setHeaderVar() {
      if (!header) return;
      var h = header.offsetHeight || 0;
      document.documentElement.style.setProperty('--dnr2-header-h', h + 'px');
      document.documentElement.style.setProperty('--dnr2-sticky-top', (h + 12) + 'px');
    }
    setHeaderVar();
    window.addEventListener('resize', setHeaderVar);

    /* ---------- announcement row: measure, then fold on scroll-down (mobile CSS) ---------- */
    var announce = document.querySelector('.dnr2-announce');
    function setAnnounceVar() {
      if (!announce || !header) return;
      header.classList.remove('is-annhidden');
      // lift the max-height cap first, or a folded row would measure as 0
      var prev = announce.style.maxHeight;
      announce.style.maxHeight = 'none';
      var h = announce.offsetHeight || 0;
      announce.style.maxHeight = prev;
      document.documentElement.style.setProperty('--dnr2-announce-h', h + 'px');
    }
    setAnnounceVar();
    window.addEventListener('resize', setAnnounceVar);
    // the message is set in a swapped webfont, so its height changes once that lands
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(setAnnounceVar); }

    if (announce && header) {
      var lastY = window.pageYOffset || 0;
      var annTicking = 0;
      window.addEventListener('scroll', function () {
        if (annTicking) return;
        annTicking = requestAnimationFrame(function () {
          annTicking = 0;
          var y = window.pageYOffset || 0;
          var delta = y - lastY;
          if (Math.abs(delta) < 6) return;          // ignore scroll jitter
          lastY = y;
          // only fold once past the row itself, so the top of the page always shows it
          if (delta > 0 && y > announce.offsetHeight) { header.classList.add('is-annhidden'); }
          else if (delta < 0) { header.classList.remove('is-annhidden'); }
        });
      }, { passive: true });
    }

    /* ---------- PDP mobile add bar: measure height → page bottom padding ---------- */
    var addBar = document.querySelector('.dnr2-sticky');
    function setAddBarVar() {
      if (!addBar) return;
      document.documentElement.style.setProperty('--dnr2-stickybar-h', (addBar.offsetHeight || 0) + 'px');
    }
    setAddBarVar();
    window.addEventListener('resize', setAddBarVar);

    /* ---------- bag badge ---------- */
    function paintBag(count) {
      document.querySelectorAll('[data-dnr-bag-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-empty', !(count > 0));
      });
    }
    window.addEventListener('dnr:bag', function (ev) {
      paintBag((ev.detail && ev.detail.count) || 0);
    });
    // bfcache restore → refresh the count
    window.addEventListener('pageshow', function (ev) {
      if (!ev.persisted) return;
      fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { paintBag(cart.item_count); })
        .catch(function () {});
    });

    /* ---------- PDP Pair & Save banner: line follows the bag count ---------- */
    var pair = document.querySelector('[data-dnr-pair]');
    if (pair) {
      var pairLine = pair.querySelector('[data-dnr-pair-line]');
      window.addEventListener('dnr:bag', function (ev) {
        var n = (ev.detail && ev.detail.count) || 0;
        var txt = n >= 2 ? pair.getAttribute('data-l2')
          : n === 1 ? pair.getAttribute('data-l1')
          : pair.getAttribute('data-l0');
        if (pairLine && txt) pairLine.textContent = txt;
        pair.classList.toggle('is-on', n >= 2);
      });
    }

    /* ---------- search panel ---------- */
    var searchPanel = document.querySelector('[data-dnr2-search]');
    var searchToggle = document.querySelector('[data-dnr2-search-toggle]');
    var searchInput = searchPanel && searchPanel.querySelector('[data-dnr2-search-input]');
    var searchResults = searchPanel && searchPanel.querySelector('[data-dnr2-search-results]');
    var searchPopular = searchPanel && searchPanel.querySelector('[data-dnr2-search-popular]');
    var searchTimer = null;

    function openSearch(open) {
      if (!searchPanel) return;
      searchPanel.hidden = !open;
      if (searchToggle) searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && searchInput) setTimeout(function () { searchInput.focus(); }, 60);
      if (!open && searchInput) { searchInput.value = ''; renderResults(''); }
    }

    function renderResults(html) {
      if (!searchResults) return;
      searchResults.innerHTML = html;
      if (searchPopular) searchPopular.hidden = html !== '';
    }

    function runSearch(q) {
      if (!q) { renderResults(''); return; }
      var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      var url = base + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8&section_id=dnr-predictive';
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (text) {
          if (!searchInput || searchInput.value.trim() !== q) return; // stale
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var inner = doc.querySelector('#shopify-section-dnr-predictive');
          renderResults(inner ? inner.innerHTML : '');
        })
        .catch(function () {});
    }

    if (searchToggle) {
      searchToggle.addEventListener('click', function () {
        openSearch(searchPanel.hidden);
      });
    }
    if (searchPanel) {
      var closeBtn = searchPanel.querySelector('[data-dnr2-search-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { openSearch(false); });
      if (searchInput) {
        searchInput.addEventListener('input', function () {
          var q = searchInput.value.trim();
          clearTimeout(searchTimer);
          searchTimer = setTimeout(function () { runSearch(q); }, 220);
        });
        searchInput.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter') return;
          var q = searchInput.value.trim();
          if (!q) return;
          e.preventDefault();
          var base = (window.routes && window.routes.search_url) || '/search';
          window.location.href = base + '?q=' + encodeURIComponent(q) + '&type=product';
        });
      }
      searchPanel.querySelectorAll('[data-dnr2-search-chip]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          if (!searchInput) return;
          searchInput.value = chip.textContent.trim();
          searchInput.focus();
          runSearch(searchInput.value.trim());
        });
      });
    }

    /* ---------- mobile drawer ---------- */
    var drawer = document.querySelector('[data-dnr2-drawer]');
    // The header's backdrop-filter makes it the containing block for
    // position:fixed descendants, so inset:0 would only cover the header bar
    // (hero shows through below). Re-parent to <body> so it fills the viewport.
    if (drawer && drawer.parentElement !== document.body) document.body.appendChild(drawer);
    function openDrawer(open) {
      if (!drawer) return;
      drawer.hidden = !open;
      document.body.style.overflow = open ? 'hidden' : '';
      document.querySelectorAll('[data-dnr2-drawer-open]').forEach(function (b) {
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    document.querySelectorAll('[data-dnr2-drawer-open]').forEach(function (b) {
      b.addEventListener('click', function () { openDrawer(true); });
    });
    document.querySelectorAll('[data-dnr2-drawer-close]').forEach(function (b) {
      b.addEventListener('click', function () { openDrawer(false); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      openSearch(false);
      openDrawer(false);
      cartDwSet(false);
    });

    /* ---------- cart drawer + cart lines (dnr-cart-drawer / dnr-cart) ---------- */
    var cartDw = document.querySelector('[data-dnr-cartdw]');
    // escape any filtered/sticky ancestors so the fixed panel spans the viewport
    if (cartDw && cartDw.parentElement !== document.body) document.body.appendChild(cartDw);
    // any click inside the fixed wrapper but outside the panel closes the drawer
    if (cartDw) {
      cartDw.addEventListener('click', function (e) {
        if (!(e.target.closest && e.target.closest('.dnr2-cartdw__panel'))) cartDwSet(false);
      });
    }

    function cartDwSet(open) {
      if (!cartDw) return;
      cartDw.hidden = !open;
      document.body.style.overflow = open ? 'hidden' : '';
    }

    function swapFromDoc(doc, selector) {
      var next = doc.querySelector(selector);
      var cur = document.querySelector(selector);
      if (!next || !cur) return;
      cur.innerHTML = next.innerHTML;
      // fresh nodes were never seen by dnr.js's reveal observer — without .in
      // they'd sit at opacity:0 (the "checkout button vanished" bug)
      cur.querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('in'); });
    }

    function refreshCartUI() {
      var jobs = [];
      if (cartDw) {
        jobs.push(fetch(window.location.pathname + '?section_id=dnr-cart-drawer')
          .then(function (r) { return r.text(); })
          .then(function (t) { swapFromDoc(new DOMParser().parseFromString(t, 'text/html'), '[data-cdw-inner]'); }));
      }
      var pg = document.querySelector('[data-dnr-cartpg]');
      if (pg) {
        jobs.push(fetch(window.location.pathname + '?section_id=' + pg.getAttribute('data-section-id'))
          .then(function (r) { return r.text(); })
          .then(function (t) { swapFromDoc(new DOMParser().parseFromString(t, 'text/html'), '[data-dnr-cartpg]'); }));
      }
      return Promise.all(jobs).catch(function () {});
    }

    window.addEventListener('dnr:cart:open', function () {
      refreshCartUI().then(function () { cartDwSet(true); });
    });
    // re-render drawer + cart page after something changes the cart out of
    // band (e.g. the welcome popup attaching a discount code)
    window.addEventListener('dnr:cart:refresh', function () { refreshCartUI(); });

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t.closest) return;
      var opener = t.closest('[data-dnr-cartdw-open]');
      if (opener) {
        e.preventDefault();
        refreshCartUI().then(function () { cartDwSet(true); });
        return;
      }
      if (t.closest('[data-dnr-cartdw-close]')) { cartDwSet(false); return; }

      var qty = t.closest('[data-cdw-qty]');
      if (qty) {
        var line = parseInt(qty.getAttribute('data-line'), 10);
        var q = parseInt(qty.getAttribute('data-cdw-qty'), 10);
        if (isNaN(line) || isNaN(q) || q < 0) return;
        qty.disabled = true;
        fetch(((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: line, quantity: q })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            window.dispatchEvent(new CustomEvent('dnr:bag', { detail: { count: cart.item_count } }));
            return refreshCartUI();
          })
          .catch(function () { qty.disabled = false; });
        return;
      }

      var apply = t.closest('[data-cdw-promo-apply]');
      if (apply) {
        var box = apply.closest('.dnr2-promo');
        var input = box && box.querySelector('[data-cdw-promo-input]');
        var msgEl = box && box.querySelector('[data-cdw-promo-msg]');
        var code = input ? input.value.trim() : '';
        if (!code || !msgEl) return;
        apply.disabled = true;
        // attach to the cart so totals update in place; fall back to the
        // checkout cookie when the cart can't take the code yet
        fetch(((window.routes && window.routes.cart_update_url) || '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: code })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            apply.disabled = false;
            var entry = (cart.discount_codes || []).filter(function (d) {
              return d.code && d.code.toLowerCase() === code.toLowerCase();
            })[0];
            msgEl.hidden = false;
            if (entry && entry.applicable) {
              msgEl.textContent = 'Code “' + code + '” applied ✦';
            } else {
              fetch('/discount/' + encodeURIComponent(code)).catch(function () {});
              msgEl.textContent = 'Code “' + code + '” saved — it will apply at checkout.';
            }
            return refreshCartUI();
          })
          .catch(function () {
            apply.disabled = false;
            msgEl.hidden = false;
            msgEl.textContent = 'Could not apply that code — try again at checkout.';
          });
      }
    });

    /* ---------- collection toolbar: price filter + count + sort ---------- */
    var ctools = document.querySelector('[data-dnr-colltools]');
    if (ctools) {
      var activePf = null;
      var applyPf = function () {
        var shown = 0;
        document.querySelectorAll('.dnr-grid [data-dnr-price]').forEach(function (c) {
          var v = parseFloat(c.getAttribute('data-dnr-price'));
          var ok = !activePf
            || (activePf === 'lt100' && v < 100)
            || (activePf === '100-200' && v >= 100 && v <= 200)
            || (activePf === 'gt200' && v > 200);
          c.style.display = ok ? '' : 'none';
          if (ok) shown++;
        });
        var cnt = ctools.querySelector('[data-dnr-count]');
        if (cnt) cnt.textContent = shown + (shown === 1 ? ' item' : ' items');
      };
      ctools.querySelectorAll('[data-dnr-pf]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          var k = chip.getAttribute('data-dnr-pf');
          activePf = activePf === k ? null : k;
          ctools.querySelectorAll('[data-dnr-pf]').forEach(function (c) {
            c.classList.toggle('is-on', c.getAttribute('data-dnr-pf') === activePf);
          });
          applyPf();
        });
      });
      var sortDd = ctools.querySelector('[data-dnr-sort]');
      if (sortDd) {
        document.addEventListener('click', function (e) {
          if (!sortDd.contains(e.target)) sortDd.open = false;
        });
      }
    }

    /* ---------- open-accordion anchors (e.g. "size guide" link) ---------- */
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-dnr-accopen]');
      if (!a) return;
      var target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });


    /* ---------- tabbed carousel ---------- */
    document.querySelectorAll('[data-dnr2-carousel]').forEach(function (sec) {
      var tabs = sec.querySelectorAll('[data-dnr2-tab]');
      var panels = sec.querySelectorAll('[data-dnr2-panel]');
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var idx = tab.getAttribute('data-dnr2-tab');
          tabs.forEach(function (t) {
            var on = t === tab;
            t.classList.toggle('is-on', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach(function (p) { p.hidden = p.getAttribute('data-dnr2-panel') !== idx; });
        });
      });
    });

    /* drag-to-scroll rows (mouse; touch scrolls natively) */
    document.querySelectorAll('[data-dnr2-drag]').forEach(function (row) {
      var drag = null;
      row.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        drag = { x: e.clientX, sl: row.scrollLeft, moved: 0 };
        row.classList.add('is-dragging');
        try { row.setPointerCapture(e.pointerId); } catch (err) {}
      });
      row.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (Math.abs(dx) > drag.moved) drag.moved = Math.abs(dx);
        row.scrollLeft = drag.sl - dx;
      });
      function end() {
        if (!drag) return;
        if (drag.moved > 6) {
          var stop = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
          row.addEventListener('click', stop, { capture: true, once: true });
          setTimeout(function () { row.removeEventListener('click', stop, { capture: true }); }, 120);
        }
        drag = null;
        row.classList.remove('is-dragging');
      }
      row.addEventListener('pointerup', end);
      row.addEventListener('pointercancel', end);
    });
  });
})();
